import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, filter, map, of, startWith, switchMap, take } from 'rxjs';
import { environment } from '../environment';

export type MaintenanceType = 'Formbuilder' | 'Utlysningsbyggare' | 'Others';
export const MAINTENANCE_TYPE = new InjectionToken<MaintenanceType>(
  'MAINTENANCE_TYPE',
  {
    providedIn: 'root',
    factory: () => 'Others',
  },
);

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  private msalService = inject(MsalService);
  private httpClient = inject(HttpClient);
  private msalBroadcastService = inject(MsalBroadcastService);
  private env = inject(environment);
  private maintenanceType = inject(MAINTENANCE_TYPE);

  private isMaintenance(): Observable<boolean> {
    let endpoint: string;
    switch (this.maintenanceType) {
      case 'Formbuilder': {
        endpoint = 'IsMaintenanceFormbuilder';
        break;
      }
      case 'Utlysningsbyggare': {
        endpoint = 'IsMaintenanceUtlysningsbyggare';
        break;
      }
      default: {
        endpoint = 'IsMaintenanceOthers';
      }
    }

    const url = `${this.env.etjansterCommonApiUrl}/UserSession/${endpoint}`;
    return this.httpClient.get<boolean>(url, httpOptions);
  }

  public hasMaintenanceRole(): boolean {
    const claimValue = 'is_allowed_in_maintenance';
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      const accounts = this.msalService.instance.getAllAccounts();
      const clientId =
        this.msalService.instance.getConfiguration().auth.clientId;
      activeAccount =
        accounts.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (a) => (a.idTokenClaims as any)?.aud === clientId,
        ) ?? accounts[0];

      if (activeAccount) {
        this.msalService.instance.setActiveAccount(activeAccount);
      }
    }

    if (!activeAccount) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userRoles = (activeAccount.idTokenClaims as any).roles;
    if (!userRoles) {
      return false;
    }

    return userRoles.includes(claimValue);
  }

  public canAccess(): Observable<boolean> {
    return this.env.name === 'Local'
      ? of(true)
      : this.isMaintenance().pipe(
          switchMap((isMaintenance) => {
            if (isMaintenance) {
              return this.msalBroadcastService.inProgress$.pipe(
                filter(
                  (status: InteractionStatus) =>
                    status === InteractionStatus.None,
                ),
                take(1),
                map(() => {
                  return this.hasMaintenanceRole();
                }),
              );
            }
            return of(true);
          }),
          startWith(true),
        );
  }
}
